import { useState, useEffect } from "react";

import { Loader2, TrendingUp, Clock, CheckCircle2, XCircle } from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";

interface RecoveryStats {
  total_recovery_attempts: number;
  successful_recoveries: number;
  failed_recoveries: number;
  unique_failures_attempted: number;
  avg_retries_to_success: number;
  avg_recovery_time_seconds: number;
  max_retry_attempts: number;
  recoveries_last_24h: number;
  recoveries_last_7_days: number;
}

const BoardRecoveryAnalytics = () => {
  const [stats, setStats] = useState<RecoveryStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const { data, error } = await supabase
        .from("board_creation_recovery_stats")
        .select("*")
        .single();

      if (error) throw error;
      setStats(data);
    } catch (error) {
      console.error("Error fetching recovery stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (!stats) {
    return null;
  }

  const successRate = stats.total_recovery_attempts > 0
    ? (stats.successful_recoveries / stats.total_recovery_attempts) * 100
    : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recovery System Analytics</CardTitle>
        <CardDescription>
          Performance metrics for automated board creation recovery
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <TrendingUp className="h-4 w-4" />
              Success Rate
            </div>
            <div className="text-2xl font-bold">{successRate.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">
              {stats.successful_recoveries} of {stats.total_recovery_attempts} attempts
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              Avg Recovery Time
            </div>
            <div className="text-2xl font-bold">
              {stats.avg_recovery_time_seconds 
                ? `${Math.round(stats.avg_recovery_time_seconds)}s`
                : 'N/A'
              }
            </div>
            <p className="text-xs text-muted-foreground">
              For successful recoveries
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              Avg Retries
            </div>
            <div className="text-2xl font-bold">
              {stats.avg_retries_to_success 
                ? stats.avg_retries_to_success.toFixed(1)
                : 'N/A'
              }
            </div>
            <p className="text-xs text-muted-foreground">
              Until success
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <XCircle className="h-4 w-4 text-destructive" />
              Max Retries
            </div>
            <div className="text-2xl font-bold">{stats.max_retry_attempts || 0}</div>
            <p className="text-xs text-muted-foreground">
              Highest retry count
            </p>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t">
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Last 24 Hours</p>
              <p className="text-xl font-semibold">{stats.recoveries_last_24h} recoveries</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Last 7 Days</p>
              <p className="text-xl font-semibold">{stats.recoveries_last_7_days} recoveries</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Unique Failures</p>
              <p className="text-xl font-semibold">{stats.unique_failures_attempted} attempted</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default BoardRecoveryAnalytics;
